import { css, html, nothing } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import type { Stroke } from '../../types/app.js';
import { isStrokeEditable, isStrokeVisible, strokeDrawingLayerId, type DrawingLayer } from '../../shared/drawing-layer.js';
import { canJoinLegend, MAX_LEGEND_LABEL, type DrawingLegendItem } from '../../shared/drawing-legend.js';
import { drawingLegendSymbol, drawingLegendSwatch } from '../drawing-legend-symbol.js';
import { t } from '../i18n.js';

interface LegendPicker {
  drawings: Stroke[];
  layers: DrawingLayer[];
  ids: string[];
  label: string;
  items: DrawingLegendItem[];
  toggle(id: string): void;
  rename(label: string): void;
  submit(existingId?: string): void;
  cancel(): void;
}

export const legendPickerStyles = css`
  .picker { margin: 0; padding: 0; border: 0; }
  .picker fieldset { border: 0; padding: 0; margin: 0 0 14px; min-width: 0; }
  .picker legend { padding: 0; margin-bottom: 8px; font-weight: 600; }
  .picker-scroll { position: relative; }
  .picker-scroll::after { content: ''; position: absolute; inset: 0 0 auto; height: 16px; pointer-events: none; box-shadow: inset 0 10px 10px -10px #17243a66; opacity: 0; }
  .picker-scroll[data-above]::after { opacity: 1; }
  .picker-more { position: absolute; inset: auto 0 0; height: 32px; display: flex; align-items: end; justify-content: center; pointer-events: none; background: linear-gradient(transparent, var(--bg-active, white)); visibility: hidden; }
  .picker-scroll[data-below] .picker-more { visibility: visible; }
  .picker-more svg { width: 28px; height: 22px; padding: 3px; color: var(--text-primary, #17243a); }
  .picker-list { max-height: min(28dvh, 240px); overflow-y: auto; scrollbar-width: thin; scrollbar-gutter: stable; overscroll-behavior: contain; display: grid; gap: 6px; }
  .picker-choice { display: flex; align-items: center; gap: 10px; min-height: 52px; padding: 8px 10px; margin: 0; border: 1px solid var(--border-color, #dbe2ec); border-radius: 10px; cursor: pointer; }
  .picker-choice:has(:checked) { border-color: #2563eb; background: var(--bg-active, #eff6ff); }
  .picker-choice:has(:disabled) { opacity: .5; cursor: default; }
  .picker-choice input { flex: none; width: 18px; min-height: 18px; height: 18px; margin: 0; padding: 0; accent-color: #2563eb; }
  .picker-choice svg { flex: none; }
  .picker-name { display: grid; min-width: 0; overflow-wrap: anywhere; }
  .picker-name small { color: var(--text-secondary, #52617a); font-size: 12px; }
  .picker-actions { position: sticky; bottom: -14px; display: flex; gap: 8px; padding: 12px 0 0; background: var(--bg-active, white); }
  .picker-actions button { flex: 1; min-height: 44px; }
  :host([adding]) { z-index: var(--layer-modal, 500); }
  .card.adding-card { width: 360px; background: var(--bg-active, white); }
  @media (width <= 768px) {
    :host([adding]) { inset: auto 0 var(--legend-keyboard-inset, 0px); max-width: none; }
    .card.adding-card { width: 100%; max-height: min(80dvh, calc(var(--legend-viewport-height, 100dvh) - 16px)); border-radius: 18px 18px 0 0; padding-bottom: 14px; overflow-y: auto; }
    .picker input[type=text] { font-size: 16px; min-height: 44px; }
    .picker-actions { padding-bottom: env(safe-area-inset-bottom); }
  }
`;

export function renderLegendPicker(picker: LegendPicker) {
  const drawings = picker.drawings.filter(stroke => stroke.tool !== 'text' && isStrokeVisible(stroke, picker.layers));
  const targets = drawings.filter(stroke => picker.ids.includes(stroke.id));
  const valid = targets.length > 0 && targets.length === picker.ids.length && targets.every(stroke => isStrokeEditable(stroke, picker.layers));
  return html`<form class="picker" @submit=${(event: SubmitEvent) => { event.preventDefault(); if (valid && picker.label.trim()) picker.submit(); }}>
    <fieldset><legend>${t('legend.choose_drawings')}</legend><div class="picker-scroll"><div class="picker-list">
      ${drawings.map((stroke, index) => html`<label class="picker-choice" title=${isStrokeEditable(stroke, picker.layers) ? '' : t('legend.locked')}>
        <input type="checkbox" .checked=${picker.ids.includes(stroke.id)} ?disabled=${!isStrokeEditable(stroke, picker.layers)} @change=${() => picker.toggle(stroke.id)}>
        <span aria-hidden="true">${unsafeSVG(drawingLegendSymbol(stroke))}</span>
        <span class="picker-name"><span>${stroke.text?.trim() || `${t(`toolbar.${stroke.tool}_tooltip`)} ${index + 1}`}</span>
          <small>${picker.layers.find(layer => layer.id === strokeDrawingLayerId(stroke))?.name}</small></span>
      </label>`)}
      ${!drawings.length ? html`<p role="status">${t('legend.no_drawings')}</p>` : nothing}
    </div><span class="picker-more" aria-hidden="true"><svg viewBox="0 0 24 16" fill="none"><path d="m6 5 6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span></div></fieldset>
    <label>${t('legend.label')}<input type="text" id="legend-label" required maxlength=${MAX_LEGEND_LABEL} .value=${picker.label}
      @input=${(event: Event) => { if (event.target instanceof HTMLInputElement) picker.rename(event.target.value); }}></label>
    ${picker.items.filter(item => !item.layer).map(item => html`<button type="button" class="existing" ?disabled=${!valid || !canJoinLegend(item, targets)} @click=${() => picker.submit(item.id)}>
      ${unsafeSVG(drawingLegendSwatch(item.symbols[0]))}${t('legend.use_existing').replace('{label}', item.label)}</button>`)}
    <div class="picker-actions"><button type="button" @click=${picker.cancel}>${t('legend.cancel')}</button>
      <button class="primary" type="submit" ?disabled=${!valid || !picker.label.trim()}>${t('legend.add')}</button></div>
  </form>`;
}

/** Reflect overflow without inserting content or changing the picker height. */
function trackPickerScroll(element: HTMLElement): () => void {
  const root = element.shadowRoot;
  if (!root) return () => {};
  let list: HTMLElement | null = null;
  const update = () => {
    const next = root.querySelector<HTMLElement>('.picker-list');
    if (next !== list) {
      resize.disconnect();
      list = next;
      if (list) resize.observe(list);
    }
    const frame = list?.parentElement;
    if (!list || !frame) return;
    frame.toggleAttribute('data-above', list.scrollTop > 1);
    frame.toggleAttribute('data-below', list.scrollHeight - list.clientHeight - list.scrollTop > 1);
  };
  const resize = new ResizeObserver(update);
  const changes = new MutationObserver(update);
  changes.observe(root, { childList: true, subtree: true, characterData: true });
  root.addEventListener('scroll', update, true);
  update();
  return () => { resize.disconnect(); changes.disconnect(); root.removeEventListener('scroll', update, true); };
}

/** Keep the phone sheet above the software keyboard without opening it on entry. */
export function trackLegendPickerViewport(element: HTMLElement): () => void {
  const stopScroll = trackPickerScroll(element);
  const view = element.ownerDocument.defaultView;
  const viewport = view?.visualViewport;
  const update = () => {
    element.style.setProperty('--legend-viewport-height', `${viewport?.height ?? view?.innerHeight ?? 900}px`);
    element.style.setProperty('--legend-keyboard-inset', `${Math.max(0, (view?.innerHeight ?? 0) - (viewport ? viewport.height + viewport.offsetTop : view?.innerHeight ?? 0))}px`);
    const input = element.shadowRoot?.querySelector<HTMLInputElement>('#legend-label');
    if (input && element.shadowRoot?.activeElement === input) view?.requestAnimationFrame(() => input.scrollIntoView({ block: 'nearest' }));
  };
  viewport?.addEventListener('resize', update); viewport?.addEventListener('scroll', update); update();
  return () => { stopScroll(); viewport?.removeEventListener('resize', update); viewport?.removeEventListener('scroll', update); };
}
